import * as React from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { Spinner } from 'spinner';
import { colors } from 'bench-style-constants';

import ReviewCheck, { status } from './ReviewCheck';

import { type JournalEntry } from './JournalEntries';
import { linkifyText } from './linkify';

const Wrapper = styled.div<{ isExpanded: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 0 32px;
  border-bottom: 1px solid ${colors.silt};
  background: ${({ isExpanded }) => (!isExpanded ? colors.pearl : colors.white)};
`;

const Row = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 24px 0;
  cursor: pointer;
`;

const Name = styled.span`
  font-size: 16px;
`;

const Description = styled.span`
  font-size: 14px;
  border-bottom: 1px solid ${colors.sand};
  padding-bottom: 16px;
  margin-left: 32px;
  white-space: pre-line;
`;

const NoChecksMessage = styled.div`
  font-size: 12px;
  margin: 16px 0;
`;

const ToggleButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 2px;
  background: ${colors.white};
  border: solid 1px ${colors.stone};
  color: ${colors.charcoal};
  padding: 0;
  font-size: 12px;
  line-height: 1;
  margin-right: 16px;
`;

const ChildContainer = styled.div<{ isExpanded: boolean }>`
  margin-left: 32px;
  display: ${({ isExpanded }) => (isExpanded ? 'block' : 'none')};
`;

const SpinnerWrap = styled.div`
  display: flex;
  justify-content: center;
  margin: 24px;
`;

const getFullDate = (yearMonth: string, endOfMonth: boolean) => {
  const date = moment(yearMonth, 'YYYY-MM');

  if (endOfMonth) {
    return date.endOf('month').format('YYYY-MM-DD');
  }

  return date.format('YYYY-MM-DD');
};

type renderReviewCheckType = {
  id: string;
  heading: string;
  description: string;
  ledgerId: string;
  code: string;
  mostIncompleteStatus: keyof typeof status;
  journalEntries: Array<JournalEntry>;
  applicableLedgerCodes: Array<string>;
  applicableLedgerIds: Array<string>;
  isApplicable: boolean;
};

const renderReviewCheck = (
  {
    id,
    heading,
    description,
    ledgerId,
    code,
    mostIncompleteStatus,
    journalEntries,
    applicableLedgerCodes,
    applicableLedgerIds,
  }: renderReviewCheckType,
  clientId: string,
  restoreLastChecks: boolean,
  startDate: string,
  endDate: string,
  onStatusUpdate: (checkId: string, ledgerId: string, checkStatus: keyof typeof status) => void,
  onSidebarToggle: (data: { journalEntryId?: string; ledgerId?: string }) => void,
) => (
  <ReviewCheck
    key={`${id}-${ledgerId || 0}`}
    id={id}
    name={heading}
    description={description}
    ledgerId={ledgerId}
    code={code}
    clientId={clientId}
    startDate={startDate}
    endDate={endDate}
    onStatusUpdate={onStatusUpdate}
    value={restoreLastChecks ? mostIncompleteStatus : null}
    journalEntries={journalEntries}
    applicableLedgerCodes={applicableLedgerCodes}
    applicableLedgerIds={applicableLedgerIds}
    onSidebarToggle={onSidebarToggle}
  />
);

export type ReviewSectionProps = {
  name: string;
  description: React.ReactNode;
  startDate: string;
  endDate: string;
  clientId: string;
  reviewChecks: renderReviewCheckType[];
  startExpanded: boolean;
  restoreLastChecks: boolean;
  loading: boolean;
  error: Record<string, unknown>;
  onCheckStatusListUpdate: (
    checkStatusList: Record<string, unknown>[],
    setByUser?: boolean,
  ) => void;
  onSidebarToggle: (data: { journalEntryId?: string; ledgerId?: string }) => void;
};

type ReviewSectionState = {
  isExpanded: boolean;
  loadedOnce: boolean;
  applicableReviewChecks: renderReviewCheckType[];
};

class ReviewSection extends React.Component<ReviewSectionProps, ReviewSectionState> {
  static defaultProps = {
    startExpanded: false,
  };

  constructor(props: ReviewSectionProps) {
    super(props);

    const { startExpanded } = props;

    this.state = {
      isExpanded: startExpanded,
      loadedOnce: false,
      applicableReviewChecks: [],
    };

    if (!props.loading) {
      const applicableReviewChecks = this.getApplicableReviewChecks(props.reviewChecks);

      this.updateCheckStatusList(props.reviewChecks);

      this.state = {
        isExpanded: startExpanded,
        loadedOnce: true,
        applicableReviewChecks,
      };
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps: ReviewSectionProps) {
    if (nextProps.loading || this.state.loadedOnce) {
      return;
    }

    const applicableReviewChecks = this.getApplicableReviewChecks(nextProps.reviewChecks);

    this.updateCheckStatusList(nextProps.reviewChecks);

    this.setState({
      loadedOnce: true,
      applicableReviewChecks,
    });
  }

  onStatusUpdate = (checkId: string, ledgerId: string, checkStatus: keyof typeof status) => {
    this.props.onCheckStatusListUpdate(
      [{ checkId, ledgerId, status: checkStatus || status.BLANK }],
      true,
    );
  };

  getApplicableReviewChecks(reviewChecks: renderReviewCheckType[]): renderReviewCheckType[] {
    return reviewChecks.filter(checkData => checkData.isApplicable);
  }

  toggleExpanded = () => {
    this.setState(prevState => ({
      isExpanded: !prevState.isExpanded,
    }));
  };

  updateCheckStatusList(reviewChecks: renderReviewCheckType[]) {
    const { onCheckStatusListUpdate, restoreLastChecks } = this.props;

    const statusList = reviewChecks.map(check => {
      const { mostIncompleteStatus } = check;
      const statusDefault =
        restoreLastChecks && mostIncompleteStatus ? mostIncompleteStatus : status.BLANK;

      return {
        checkId: check.id,
        ledgerId: check.ledgerId,
        status: check.isApplicable ? statusDefault : status.NOT_APPLICABLE,
      };
    });

    onCheckStatusListUpdate(statusList);
  }

  render() {
    const { isExpanded } = this.state;

    const {
      name,
      description,
      clientId,
      startDate,
      endDate,
      restoreLastChecks,
      loading,
      error,
      onSidebarToggle,
    } = this.props;

    const { applicableReviewChecks } = this.state;

    return (
      <Wrapper isExpanded={isExpanded}>
        <Row onClick={this.toggleExpanded}>
          <ToggleButton>{isExpanded ? '-' : '+'}</ToggleButton>
          <Name>{name}</Name>
        </Row>
        {isExpanded && (
          <Description>
            {typeof description === 'string' ? linkifyText(description) : description}
          </Description>
        )}
        <ChildContainer isExpanded={isExpanded}>
          {loading && (
            <SpinnerWrap>
              <Spinner />
            </SpinnerWrap>
          )}
          {error && `Error loading section: ${error.message}`}
          {applicableReviewChecks.length === 0 && (
            <NoChecksMessage>No checks present for the period selected.</NoChecksMessage>
          )}
          {applicableReviewChecks.length > 0 &&
            applicableReviewChecks.map(checkData =>
              renderReviewCheck(
                checkData,
                clientId,
                restoreLastChecks,
                getFullDate(startDate, false),
                getFullDate(endDate, true),
                this.onStatusUpdate,
                onSidebarToggle,
              ),
            )}
        </ChildContainer>
      </Wrapper>
    );
  }
}

export default ReviewSection;
